import React, { useState, useEffect, useContext, useCallback, useRef } from "react";
import { useAuth } from "../hooks/AuthProvider";
import Spinner from "../Components/Spinner";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import axios from "axios";
import { BASE_URL_API } from "../Constants";
import "../widgets/widget.css";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import DataGridTable from "../Components/DataGridTable";
import { GridActionsCellItem } from "@mui/x-data-grid";
import { Tooltip } from "@mui/material";
import { AssetIcon } from "../Icons";

import { LanguageContext } from "../context/LanguageContext";
import assetTranslations from "../languages/asset.json";

import { getErrorMessage } from "../utils/errorUtils";
import { AddAssetCard } from "./AddAssetCard";
import { EditAssetCard } from "./EditAssetCard";
import { deleteAssetById, deleteMultiAssetCards } from "../lib/actions";
import AppDialog from "../Components/AppDialog"; 
import { normalizeAssetCardListRow } from "../utils/listRowIds";

export const AssetCards = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [addAssetCard, setAddAssetCard] = useState(false);
    const [editAssetCardId, setEditAssetCardId] = useState(null);
    const navigate = useNavigate();

    const { lang, t } = useContext(LanguageContext);
    const assetT = assetTranslations[lang];
    const isRtl = ["ar", "ur"].includes(lang);
    
    const resolverRef = useRef(null);
    const [dialog, setDialog] = useState({
        open: false,
        type: "alert", // "alert" | "confirm"
        variant: "info", // "info" | "danger" | "error"
        icon: "alert", // "alert" | "delete"
        title: "",
        message: "",
        confirmText: "Ok",
        cancelText: "Cancel",
        showCancel: false,
    });

    const closeDialog = useCallback((result = false) => {
        setDialog((d) => ({ ...d, open: false }));
        if (resolverRef.current) {
            resolverRef.current(result);
            resolverRef.current = null;
        }
    }, []);

    const alert = useCallback(
        (opts) =>
            new Promise((resolve) => {
                resolverRef.current = resolve;

                setDialog({
                    open: true,
                    type: opts?.type || "alert",
                    variant: opts?.variant || "info",
                    icon: opts?.icon || "alert",
                    title: opts?.title || "",
                    message: opts?.message || "",
                    confirmText: opts?.confirmText || "Ok",
                    cancelText: opts?.cancelText || "Cancel",
                    showCancel: Boolean(opts?.showCancel),
                });
            }),
        []
    );

    const showAlert = useCallback(
        async (type, message) => {
            const variant =
                type === "error" ? "error" : type === "warning" ? "info" : "info";

            await alert({
                type: "alert",
                variant,
                icon: "alert",
                title:
                    type === "error"
                        ? (assetT?.alert?.errorTitle || "Error")
                        : type === "warning"
                            ? (assetT?.alert?.warningTitle || "Warning")
                            : (assetT?.alert?.infoTitle || "Info"),
                message: message || "",
                confirmText: "Ok",
            });
        },
        [alert, assetT?.alert?.errorTitle, assetT?.alert?.warningTitle, assetT?.alert?.infoTitle]
    );

    const { permissions, org } = useAuth();
    const pagingoptions = [10, 20, 50, 100];
    const canCreateAssets = permissions["create_assets"];
    const canEditAssets = permissions["edit_assets"];
    const canDeleteAssets = permissions["delete_assets"];
    const canViewAssets = permissions["view_assets"];

    /* ------------------------------------------------------------------ */
    /* COLUMNS                                                             */
    /* ------------------------------------------------------------------ */

    const baseColumns = [
        {
            field: "deviceName",
            headerName: assetT.column.deviceName,
            width: 150,
            align: "left",
            headerAlign: "left",
            headerClassName: "font-responsive",
            cellClassName: "font-responsive",
        },
        {
            field: "asset_name",
            headerName: assetT.column.assetName,
            width: 200,
            align: "left",
            headerAlign: "left",
            headerClassName: "font-responsive",
            cellClassName: "font-responsive"
        },
        {
            field: "asset_code",
            headerName: assetT.column.assetCode,
            width: 120,
            align: "center",
            headerAlign: "center",
            headerClassName: "font-responsive",
            cellClassName: "font-responsive"
        },
        {
            field: "category_subcategory",
            headerName: assetT.column.categorySubcategory,
            width: 200,
            align: "left",
            headerAlign: "left",
            headerClassName: "font-responsive",
            cellClassName: "font-responsive"
        },
        {
            field: "brand",
            headerName: assetT.column.brand,
            width: 100,
            align: "left",
            headerAlign: "left",
            headerClassName: "font-responsive",
            cellClassName: "font-responsive"
        },
        {
            field: "model_number",
            headerName: assetT.column.modelNumber,
            width: 120,
            align: "center",
            headerAlign: "center",
            headerClassName: "font-responsive",
            cellClassName: "font-responsive"
        },
        {
            field: "serial_number",
            headerName: assetT.column.serialNumber,
            width: 150,
            align: "center",
            headerAlign: "center",
            headerClassName: "font-responsive",
            cellClassName: "font-responsive"
        },
        {
            field: "type",
            headerName: assetT.column.type,
            width: 100,
            align: "left",
            headerAlign: "left",
            headerClassName: "font-responsive",
            cellClassName: "font-responsive"
        },
        {
            field: "location",
            headerName: assetT.column.location,
            width: 200,
            align: "left",
            headerAlign: "left",
            headerClassName: "font-responsive",
            cellClassName: "font-responsive"
        },
        {
            field: "manufacture_date",
            headerName: assetT.column.manufactureDate,
            width: 150,
            align: "center",
            headerAlign: "center",
            type: "date",
            valueFormatter: value =>
                value ? moment(value).format("DD/MM/YYYY HH:mm:ss") : "",
            headerClassName: "font-responsive",
            cellClassName: "font-responsive"
        },
        {
            field: "capacity_rating",
            headerName: assetT.column.capacityRating,
            width: 120,
            align: "center",
            headerAlign: "center",
            type: "number",
            headerClassName: "font-responsive",
            cellClassName: "font-responsive"
        },
        {
            field: "running_hours_counter",
            headerName: assetT.column.runningHoursCounter,
            width: 150,
            align: "center",
            headerAlign: "center",
            type: "number",
            headerClassName: "font-responsive",
            cellClassName: "font-responsive"
        },
        {
            field: "condition_status",
            headerName: assetT.column.conditionStatus,
            width: 150,
            align: "center",
            headerAlign: "center",
            type: "string",
            headerClassName: "font-responsive",
            cellClassName: "font-responsive"
        },
        {
            field: "purchase_cost",
            headerName: assetT.column.purchaseCost,
            width: 120,
            align: "right",
            headerAlign: "center",
            type: "number",
            headerClassName: "font-responsive",
            cellClassName: "font-responsive"
        }
    ];

    const canShowActionsColumn = canEditAssets || canDeleteAssets;

    const columns = canShowActionsColumn
        ? [
              {
                  field: "actions",
                  headerName: assetT.column.actions,
                  type: "actions",
                  width: 100,
                  getActions: params =>
                      [
                          canEditAssets ? (
                              <GridActionsCellItem
                                  key="edit"
                                  icon={
                                      <Tooltip title={assetT.tooltipEdit}>
                                          <EditIcon
                                              style={{
                                                  fontSize:
                                                      "calc(var(--body-font-size) * var(--font-size-multiplier))"
                                              }}
                                          />
                                      </Tooltip>
                                  }
                                  showInMenu
                                  label={assetT.tooltipEdit}
                                  onClick={event => {
                                      event.stopPropagation();
                                      setEditAssetCardId(params.row.id);
                                  }}
                              />
                          ) : null,
                          canDeleteAssets ? (
                              <GridActionsCellItem
                                  key="delete"
                                  icon={
                                      <Tooltip title={assetT.tooltipDelete}>
                                          <DeleteIcon
                                              style={{
                                                  fontSize:
                                                      "calc(var(--body-font-size) * var(--font-size-multiplier))"
                                              }}
                                          />
                                      </Tooltip>
                                  }
                                  showInMenu
                                  label={assetT.tooltipDelete}
                                  onClick={event => {
                                      event.stopPropagation();
                                      deleteAssetById(
                                          params.row.id,
                                          params.row.asset_name,
                                          org.id,
                                          null,
                                          alert,
                                          () => t.actions
                                      );
                                  }}
                              />
                          ) : null
                      ].filter(Boolean)
              },
              ...baseColumns
          ]
        : baseColumns;

    /* ------------------------------------------------------------------ */
    /* DATA FETCH                                                          */
    /* ------------------------------------------------------------------ */

    const fetchAssets = useCallback(async () => {
        if (!org?.id) {
            setRows([]);
            setLoading(false);
            return;
        }

        setLoading(true);

        try {
            const res = await axios.get(
                `${BASE_URL_API}/asset-card/by-organization?organization_id=${org.id}`
            );
            if (res.data && Array.isArray(res.data.data)) {
                const preprocessedRows = res.data.data
                    .map(normalizeAssetCardListRow)
                    .map(row => {
                    const deviceNameValue = (row.Device?.name || "").toLowerCase();

                    const capacityRatingValue =
                        row.OperationalData?.capacity_rating !== undefined &&
                        row.OperationalData?.capacity_rating !== null
                            ? Number(row.OperationalData.capacity_rating)
                            : null;
                    const runningHoursCounterValue =
                        row.OperationalData?.running_hours_counter !== undefined &&
                        row.OperationalData?.running_hours_counter !== null
                            ? Number(row.OperationalData.running_hours_counter)
                            : null;
                    const purchaseCostValue =
                        row.FinancialOwnership?.purchase_cost !== undefined &&
                        row.FinancialOwnership?.purchase_cost !== null
                            ? Number(row.FinancialOwnership.purchase_cost)
                            : null;
                    const conditionStatusValue = (
                        row.MaintenanceLifecycle?.condition_status || ""
                    ).toLowerCase();

                    return {
                        ...row,
                        deviceName: deviceNameValue,
                        _originalDeviceName: row.Device?.name || "N/A",
                        capacity_rating: capacityRatingValue,
                        running_hours_counter: runningHoursCounterValue,
                        purchase_cost: purchaseCostValue,
                        condition_status: conditionStatusValue
                    };
                });
                setRows(preprocessedRows);
            } else {
                console.error(
                    "Received unexpected data format from /asset-card/by-organization:",
                    res.data
                );
                setRows([]);
                await showAlert("error", "Received an invalid format from the server.");
            }
        } catch (err) {
            const errorMessage = getErrorMessage(err);
            await showAlert("error", errorMessage);
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [org?.id, showAlert]);

    useEffect(() => {
        fetchAssets();
    }, [fetchAssets]);

    /* ------------------------------------------------------------------ */
    /* BULK ACTIONS                                                        */
    /* ------------------------------------------------------------------ */

    const [selectedRows, setSelectedRows] = React.useState([]);

    const selectActions = canDeleteAssets
        ? [
              {
                  title: "Delete",
                  icon: (
                      <DeleteIcon
                          style={{
                              color: "rgb(236, 69, 78)",
                              marginInlineEnd: "20px",
                              fontSize:
                                  "calc(var(--body-font-size) * var(--font-size-multiplier))"
                          }}
                      />
                  ),
                  action: async ids => {
                      await deleteMultiAssetCards(
                          selectedRows,
                          org.id,
                          fetchAssets,
                          alert, 
                          () => t.actions
                      );
                      setSelectedRows([]);
                  }
              }
          ].filter(Boolean)
        : [];

    /* ------------------------------------------------------------------ */
    /* ROW CLICK                                                           */
    /* ------------------------------------------------------------------ */

    const handleRowClick = (params, event) => {
        if (
            event?.target?.closest(".MuiDataGrid-cell--actions") ||
            event?.target?.closest('[role="menuitem"]')
        ) {
            return;
        }
        navigate("/asset-card/details/" + params.row.id);
    };

    /* ------------------------------------------------------------------ */
    /* RENDER                                                              */
    /* ------------------------------------------------------------------ */

    return canViewAssets ? (
        <div
            className="configuration devices-page asset-page"
            style={{ display: "flex", flexDirection: "column", height: "100%" }}
            dir={isRtl ? "rtl" : "ltr"}
        >
            <AppDialog
                open={dialog.open}
                type={dialog.type}
                variant={dialog.variant}
                icon={dialog.icon}
                title={dialog.title}
                message={dialog.message}
                confirmText={dialog.confirmText}
                cancelText={dialog.cancelText}
                showCancel={dialog.showCancel}
                onConfirm={() => closeDialog(true)}
                onCancel={() => closeDialog(false)}
                onClose={() => closeDialog(false)}
            />

            {addAssetCard && (
                <AddAssetCard
                    state={addAssetCard}
                    setState={setAddAssetCard}
                    refresh={fetchAssets}
                />
            )}
            {editAssetCardId && (
                <EditAssetCard
                    assetId={editAssetCardId}
                    setState={setEditAssetCardId}
                    refresh={fetchAssets}
                />
            )}

            <div className="device-main">
                <div className="devices-header">
                    <div className="devices-header-left">
                        <span style={{ marginInlineEnd: 8 }}>
                            <AssetIcon color={"var(--accent)"} />
                        </span>
                        <h2 className="devices-title" style={{ color: "#06919f" }}>
                            {assetT.assetsPageTitle}
                        </h2>
                    </div>

                    {canCreateAssets ? (
                        <button
                            className="devices-primary-btn"
                            onClick={() => setAddAssetCard(true)}
                        >
                            <span>{assetT.newAsset}</span>
                        </button>
                    ) : null}
                </div>

                {loading ? (
                    <Spinner />
                ) : rows.length > 0 ? (
                    <div
                        className="devices-grid-wrapper"
                        style={{
                            flex: 1,
                            minHeight: 0,
                            marginTop: "20px"
                        }}
                    >
                        <DataGridTable
                            columns={columns}
                            rows={rows}
                            idcolumn={"id"}
                            pagingoptions={pagingoptions}
                            sortField="id"
                            sortType="asc"
                            handleRowClick={handleRowClick}
                            checkboxSelection={canDeleteAssets}
                            rowSelectionModel={selectedRows}
                            setRowSelectionModel={setSelectedRows}
                            selectActions={selectActions}
                            showToolbar={true}
                            tableKey="assetCards"
                        />
                    </div>
                ) : (
                    <div
                        style={{
                            width: "100%",
                            flex: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize:
                                "calc(var(--body-font-size) * var(--font-size-multiplier))"
                        }}
                    >
                        {assetT.noData}
                    </div>
                )}
            </div>
        </div>
    ) : null;
};
